/**
 * Countries and regions, as codes.
 *
 * The same names appear twice over — once as a holding's country, once as a
 * slice of a geographic allocation — and both stored the full string every
 * time. "United States" is 15 characters written out 325 times.
 *
 * ISO 3166-1 alpha-2 where the value is a country, so the codes are the ones
 * everything else in the world uses. Issuers also publish regions and a
 * catch-all, which have no ISO code; those get their own and are marked, so a
 * caller that needs actual countries can tell them apart.
 *
 * Resolution is per value, on demand. Nothing expands the dataset.
 */

export interface PlaceMeta {
    name: string;
    /** False for regions and catch-alls — "Europe", "Other". */
    country: boolean;
}

export const PLACES: Record<string, PlaceMeta> = {
    AT: { name: 'Austria', country: true },
    AU: { name: 'Australia', country: true },
    CA: { name: 'Canada', country: true },
    CH: { name: 'Switzerland', country: true },
    CN: { name: 'China', country: true },
    DE: { name: 'Germany', country: true },
    DK: { name: 'Denmark', country: true },
    ES: { name: 'Spain', country: true },
    FI: { name: 'Finland', country: true },
    FR: { name: 'France', country: true },
    GB: { name: 'United Kingdom', country: true },
    IT: { name: 'Italy', country: true },
    JP: { name: 'Japan', country: true },
    NL: { name: 'Netherlands', country: true },
    NO: { name: 'Norway', country: true },
    NZ: { name: 'New Zealand', country: true },
    PT: { name: 'Portugal', country: true },
    SE: { name: 'Sweden', country: true },
    SG: { name: 'Singapore', country: true },
    US: { name: 'United States', country: true },
    ZA: { name: 'South Africa', country: true },

    // Not countries. Issuers publish these as allocation slices alongside the
    // real ones, so they need codes but should not be counted as countries.
    EU: { name: 'Europe', country: false },
    AP: { name: 'Asia / Australia', country: false },
    XX: { name: 'Other', country: false },
};

export const placeOf = (code: string): PlaceMeta | null => PLACES[code] ?? null;
export const nameOfPlace = (code: string): string => PLACES[code]?.name ?? code;

/**
 * Names an issuer publishes that are not the canonical one.
 *
 * "Britain" and "United Kingdom" are the same place. Left as two strings they
 * would need two codes, and a chart would show the country twice.
 */
export const ALIASES: Record<string, string> = {
    britain: 'GB',
    'great britain': 'GB',
    uk: 'GB',
    usa: 'US',
    'u.s.': 'US',
};

const BY_NAME: Record<string, string> = Object.fromEntries(Object.entries(PLACES).map(([code, meta]) => [meta.name.toLowerCase(), code]));

/** The code for a published name, or null if it is one we have not seen. */
export const codeForPlace = (name: string): string | null => {
    const key = name.trim().toLowerCase();
    return BY_NAME[key] ?? ALIASES[key] ?? null;
};
